// src/pages/conversaciones/ConversacionesPage.tsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Avatar, Badge, StatusBadge } from '@/components/ui';
import { QDataTable } from '@/components/tables';
import { Plus } from 'lucide-react';
import { truncate } from '@/lib/utils';
import { useConversaciones } from '@/hooks/queries/useConversaciones';
import { useScreenState } from '@/hooks/useScreenState';
import { buildTableProps } from '@/lib/screen-utils';
import { conversacionScreenDef } from '@/screen-definitions';
import { ConversacionFormModal } from './components';
import type { Conversacion, ConversacionFiltros, EstadoConversacion } from '@/types';

export function ConversacionesPage() {
  const navigate = useNavigate();
  const screenState = useScreenState(conversacionScreenDef);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Build entity-specific query params from screen state
  const queryFiltros = useMemo<ConversacionFiltros>(() => {
    const estados = screenState.quickFilterValues.estado;
    return {
      search: screenState.search || undefined,
      estado: estados?.length === 1 ? estados[0] as EstadoConversacion : undefined,
    };
  }, [screenState.search, screenState.quickFilterValues]);

  const query = useConversaciones(queryFiltros);

  const tableProps = buildTableProps(conversacionScreenDef, screenState, query, {
    getId: (c: Conversacion) => c.id,
    onRowClick: (c: Conversacion) => navigate(`/conversaciones/${c.id}`),
    customRenderers: {
      cliente: (row: Conversacion) => (
        <div className="flex items-center gap-3">
          <Avatar fallback={row.cliente?.nombre} size="sm" />
          <div className="min-w-0">
            <div className="font-medium">{row.cliente?.nombre}</div>
            {row.empresa && (
              <div className="text-xs text-muted-foreground">{row.empresa.nombre}</div>
            )}
          </div>
        </div>
      ),
      asunto: (row: Conversacion) => (
        <div className="min-w-0">
          <div className="text-sm font-medium">{row.asunto || 'Sin asunto'}</div>
          <div className="text-xs text-muted-foreground truncate max-w-xs">
            {row.ultimo_mensaje ? truncate(row.ultimo_mensaje, 80) : 'Sin mensajes'}
          </div>
        </div>
      ),
      estado: (row: Conversacion) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={row.estado} />
          {(row.no_leidos_count ?? 0) > 0 && (
            <Badge variant="danger">{row.no_leidos_count}</Badge>
          )}
        </div>
      ),
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conversaciones</h1>
          <p className="text-muted-foreground">Gestiona las conversaciones con clientes</p>
        </div>
      </div>

      <QDataTable
        {...tableProps}
        headerActions={
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Conversación
          </Button>
        }
      />

      <ConversacionFormModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
